import { Injectable, inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';

/**
 * SEO service for managing meta tags and page titles dynamically
 * Provides methods to update page metadata for better search engine optimization
 */
@Injectable({
  providedIn: 'root'
})
export class SeoService {
  // Angular services for managing page title and meta tags
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);

  /**
   * Updates the page title
   * @param title - The new page title to set
   */
  setTitle(title: string): void {
    this.title.setTitle(title);
  }

  /**
   * Updates meta tags for SEO and social media sharing
   * @param config - Configuration object containing meta tag values
   */
  updateMetaTags(config: {
    title?: string;
    description?: string;
    keywords?: string;
    image?: string;
    url?: string;
    type?: string;
  }): void {
    // Update page title if provided
    if (config.title) {
      this.setTitle(config.title);
      this.meta.updateTag({ name: 'title', content: config.title });
      this.meta.updateTag({ property: 'og:title', content: config.title });
      this.meta.updateTag({ property: 'twitter:title', content: config.title });
    }

    // Update description meta tags
    if (config.description) {
      this.meta.updateTag({ name: 'description', content: config.description });
      this.meta.updateTag({ property: 'og:description', content: config.description });
      this.meta.updateTag({ property: 'twitter:description', content: config.description });
    }

    // Update keywords meta tag
    if (config.keywords) {
      this.meta.updateTag({ name: 'keywords', content: config.keywords });
    }

    // Update Open Graph and Twitter image tags
    if (config.image) {
      this.meta.updateTag({ property: 'og:image', content: config.image });
      this.meta.updateTag({ property: 'twitter:image', content: config.image });
    }

    // Update URL meta tags
    if (config.url) {
      this.meta.updateTag({ property: 'og:url', content: config.url });
      this.meta.updateTag({ property: 'twitter:url', content: config.url });
      this.meta.updateTag({ rel: 'canonical', href: config.url });
    }

    // Update Open Graph type
    if (config.type) {
      this.meta.updateTag({ property: 'og:type', content: config.type });
    }
  }

  /**
   * Adds structured data (JSON-LD) to the page for enhanced search results
   * @param structuredData - The JSON-LD structured data object
   */
  addStructuredData(structuredData: object): void {
    // Check if script already exists
    const existingScript = document.querySelector('script[type="application/ld+json"]');

    // Remove existing script if found
    if (existingScript) {
      existingScript.remove();
    }

    // Create new script element for structured data
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(structuredData);

    // Append to document head
    document.head.appendChild(script);
  }
}
